import { z } from 'zod';
import { api } from '../../../lib/api/client';
import { payloadViewSchema } from '../../../lib/api/shipments';

// S19 · Activity › Deliveries (07 §5): every payload and note across
// shipments, Dokless's health, the dead letter. Mirrored from
// apps/api/src/modules/outbound/deliveries-console.dto.ts (and the connection
// test of settings for «Prova adesso»: GET /ping only). Unknown keys pass
// through. Relative imports: the API's integration spec loads this file.

export const DELIVERY_STATES = [
  'pending',
  'sending',
  'delivered',
  'rejected',
  'retry',
  'dead',
  'superseded',
  'shadow',
] as const;
export const DELIVERY_KINDS = ['full', 'position', 'note'] as const;
/** The payloads' causes (trigger codes of the outbox). */
export const DELIVERY_CAUSES = [
  'read',
  'ais',
  'recompute',
  'user',
  'manual_resend',
  'dokless',
  'outbox_sweep',
  'read_failed',
  'release_to_manual',
] as const;
export const RANGES = ['today', '24h', '7d', 'all'] as const;
export type Range = (typeof RANGES)[number];

export const deliveryItemSchema = z.looseObject({
  id: z.string(),
  type: z.enum(['payload', 'note']),
  shipment_id: z.string(),
  reference: z.string().nullable(),
  carrier_code: z.string().nullable(),
  kind: z.string(),
  revision: z.number().nullable(),
  cause: z.string().nullable(),
  level: z.string().nullable(),
  state: z.string(),
  attempts: z.number(),
  next_attempt_at: z.string().nullable(),
  sent_at: z.string().nullable(),
  response_status: z.number().nullable(),
  error: z.string().nullable(),
  created_at: z.string(),
  // Added with S19 (optional so an older API still parses).
  message: z.string().nullable().optional(),
  superseded_by_id: z.string().nullable().optional(),
});
export type DeliveryItem = z.infer<typeof deliveryItemSchema>;

export const deliveryListSchema = z.looseObject({
  items: z.array(deliveryItemSchema),
  next_cursor: z.string().nullable(),
  total: z.number().optional(),
});
export type DeliveryList = z.infer<typeof deliveryListSchema>;

export const retryResultSchema = z.looseObject({
  retried: z.number(),
  shipments: z.number(),
  writes_enabled: z.boolean(),
});
export type RetryResult = z.infer<typeof retryResultSchema>;

export const doklessHealthSchema = z.looseObject({
  writes_enabled: z.boolean(),
  last_ping_ok_at: z.string().nullable(),
  last_pull_ok_at: z.string().nullable(),
  last_list_ok_at: z.string().nullable(),
  last_error: z.looseObject({ kind: z.string(), at: z.string(), error: z.string() }).nullable(),
  /** The client times every GET: last ping (status, ms), p95 of the window, last 5xx. */
  latency: z
    .looseObject({
      last_ping: z.looseObject({ at: z.string(), ms: z.number(), status: z.number().nullable() }).nullable(),
      p95_ms: z.number().nullable(),
      samples: z.number(),
      window_minutes: z.number(),
      last_5xx: z.looseObject({ at: z.string(), status: z.number(), kind: z.string() }).nullable(),
    })
    .optional(),
  client_errors_last_hour: z.number(),
  server_errors_last_hour: z.number(),
  counts: z.record(z.string(), z.union([z.number(), z.boolean()])),
});
export type DoklessHealth = z.infer<typeof doklessHealthSchema>;

export const connectionTestSchema = z.looseObject({
  name: z.string(),
  ok: z.boolean(),
  at: z.string(),
  detail: z.string(),
});

export { payloadViewSchema };

export interface DeliveryFilters {
  state?: (typeof DELIVERY_STATES)[number];
  kind?: (typeof DELIVERY_KINDS)[number];
  cause?: string;
  carrier?: string;
  range?: Range;
}

const enc = encodeURIComponent;

/** From when the range starts (`today` = local midnight); null = no lower bound. */
export function rangeSince(range: Range, now: number): string | null {
  if (range === 'all') return null;
  if (range === '24h') return new Date(now - 86_400_000).toISOString();
  if (range === '7d') return new Date(now - 7 * 86_400_000).toISOString();
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

export const deliveriesApi = {
  list: (filters: DeliveryFilters, now: number, limit = 100) => {
    const q = new URLSearchParams();
    if (filters.state) q.set('state', filters.state);
    if (filters.kind) q.set('kind', filters.kind);
    if (filters.cause) q.set('cause', filters.cause);
    if (filters.carrier) q.set('carrier', filters.carrier);
    const since = rangeSince(filters.range ?? 'today', now);
    if (since) q.set('since', since);
    q.set('limit', String(limit));
    return api(`/internal/deliveries?${q}`, { schema: deliveryListSchema });
  },
  dead: () => api('/internal/deliveries?state=dead&limit=100', { schema: deliveryListSchema }),
  payload: (id: string) => api(`/internal/deliveries/${enc(id)}`, { schema: payloadViewSchema }),
  health: () => api('/internal/dokless/health', { schema: doklessHealthSchema }),
  retryDead: () => api('/internal/deliveries/retry-dead', { method: 'POST', schema: retryResultSchema }),
  ping: () =>
    api('/internal/settings/connections/dokless/test', { method: 'POST', schema: connectionTestSchema }),
};

export const deliveriesKeys = {
  all: ['activity', 'deliveries'] as const,
  list: (filters: DeliveryFilters) => ['activity', 'deliveries', 'list', filters] as const,
  dead: ['activity', 'deliveries', 'dead'] as const,
  payload: (id: string) => ['activity', 'deliveries', 'payload', id] as const,
  health: ['activity', 'deliveries', 'health'] as const,
};
